import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utility";
import type { Hotel, HotelRoom } from "src/store/slices/hotels/Hotels";
import type { PaymentState } from "src/Modules/Hotels/constants";

export type PriceLineItem = {
  label: string;
  value: string;
};

export type PriceBreakdown = {
  baseFare: number;
  totalTax: number;
  addonsTotal: number;
  totalPrice: number;
  pointsEquivalent: number;
  currency?: string | null;
  originalCurrency?: string | null;
  totalOriginalFare?: number | null;
  lineItems: PriceLineItem[];
  cashToPay: number;
  pointsToPay: number;
  pointsApplied?: {
    pointsUsed: number;
    deductionAmount: number;
  };
  formattedTotalCash: string;
  formattedTotalPoints: string;
  formattedTotalOriginalCash?: string;
};

type PriceBreakdownParams = {
  hotel: Hotel;
  selectedRoom: HotelRoom | null;
  pointsFactor: number;
  paymentState?: PaymentState;
  overrideTotalFare?: number;
};

export function usePriceBreakdown({ hotel, selectedRoom, pointsFactor, paymentState, overrideTotalFare }: PriceBreakdownParams): PriceBreakdown {
  const { t } = useTranslation();
  return useMemo(() => {
    const currency = hotel?.currency || t("Flights.details.payment.currencyFallback");
    const totalPrice = overrideTotalFare ?? selectedRoom?.totalFare ?? 0;
    const totalTax = selectedRoom?.totalTax ?? 0;
    const baseFare = totalPrice - totalTax;
    const pointsEquivalent = Math.round(totalPrice * pointsFactor);

    const hasPaymentState = !!paymentState && paymentState.pointsToPay > 0;

    const pointsApplied = hasPaymentState
      ? { pointsUsed: paymentState!.pointsToPay, deductionAmount: paymentState!.pointsToPay / pointsFactor }
      : undefined;

    const cashToPay = hasPaymentState ? paymentState!.cashToPay : totalPrice;
    const pointsToPay = hasPaymentState ? paymentState!.pointsToPay : pointsEquivalent;

    const formattedTotalCash = `${currency} ${formatNumber(cashToPay)}`;
    const formattedTotalPoints = `${formatNumber(pointsToPay)} ${t("Flights.details.summary.pts")}`;

    const originalCurrency = hotel?.originalCurrency;
    const totalOriginalFare = selectedRoom?.totalOriginalFare;
    const formattedTotalOriginalCash =
      totalOriginalFare != null && originalCurrency ? `${originalCurrency} ${formatNumber(totalOriginalFare)}` : undefined;

    const extraGuestCharges = selectedRoom?.extraGuestCharges ?? 0;
    const supplementsTotal = selectedRoom?.supplements?.reduce((sum: number, s: { price?: number }) => sum + (s.price ?? 0), 0) ?? 0;
    const addonsTotal = extraGuestCharges + supplementsTotal;

    const lineItems: PriceLineItem[] = [
      { label: t("Flights.details.summary.faresSubtotal"), value: `${currency} ${formatNumber(baseFare)}` },
    ];

    if (addonsTotal > 0) {
      lineItems.push({ label: t("Flights.details.summary.addonsAndExtras"), value: `${currency} ${formatNumber(addonsTotal)}` });
    }

    return {
      baseFare,
      totalTax,
      addonsTotal,
      totalPrice,
      pointsEquivalent,
      currency,
      originalCurrency,
      totalOriginalFare,
      lineItems,
      cashToPay,
      pointsToPay,
      pointsApplied,
      formattedTotalCash,
      formattedTotalPoints,
      formattedTotalOriginalCash,
    };
  }, [
    hotel?.currency,
    hotel?.originalCurrency,
    selectedRoom?.totalFare,
    selectedRoom?.totalTax,
    selectedRoom?.totalOriginalFare,
    selectedRoom?.extraGuestCharges,
    selectedRoom?.supplements,
    pointsFactor,
    paymentState,
    overrideTotalFare,
    t,
  ]);
}
